import Fuse from "fuse.js";

import type { IImportedKeywords, IKeyword } from "app/pages/digital-marketing/keywords-campaigns/shared/types";

export function findMostSimilarColumnName(columns: string[], lookFor: string): number {
    if (!columns.length) {
        return -1;
    }

    const normalized = lookFor.trim();
    if (!normalized) {
        return -1;
    }

    if (normalized.toLowerCase() === "target url") {
        const urlIndex = columns.findIndex((col) => col.toLowerCase() === "url");
        if (urlIndex !== -1) {
            return urlIndex;
        }
    }

    const items = columns.map((name, index) => ({ name, index }));
    const fuse = new Fuse(items, {
        includeScore: false,
        threshold: 0.5,
        keys: ["name"],
        ignoreLocation: true,
        minMatchCharLength: 2,
    });
    const result = fuse.search(normalized, { limit: 1 });

    if (result.length === 0) {
        return -1;
    }

    const [firstItem] = result;

    return firstItem.index;
}

interface IMappingConfig {
    keyword: number;
    tags: number;
    targetUrl: number;
}

/**
 * Parses imported data from CSV into the IImportedKeywords format
 * @param data - The raw data from the CSV file
 * @param startRow - The row to start parsing from (0 if no headers, 1 if headers)
 * @param mappingConfig - Configuration mapping column indices to keyword, tags, and targetUrl
 * @param id - Unique identifier for the import source
 * @param fileName - Name of the file being imported
 * @returns Parsed keywords in IImportedKeywords format
 */
export function parseImportedKeywords(
    data: string[][],
    startRow: number,
    mappingConfig: IMappingConfig,
    id: string,
    fileName: string,
): IImportedKeywords {
    const keywordIdx = mappingConfig.keyword;
    const tagsIdx = mappingConfig.tags;
    const targetUrlIdx = mappingConfig.targetUrl;

    const keywords: IKeyword[] = new Array(data.length - startRow);
    let validKeywordsCount = 0;

    const emptySearchEngines: never[] = [];

    const tagsCache = new Map<string, string[]>();

    for (let i = startRow; i < data.length; i++) {
        const row = data[i];
        const keywordValue = row[keywordIdx]?.toLowerCase().trim();

        if (!keywordValue) {
            continue;
        }

        let tags: string[] = [];
        if (row[tagsIdx]) {
            const tagsString = row[tagsIdx];
            const cachedTags = tagsCache.get(tagsString);

            if (cachedTags) {
                tags = cachedTags;
            } else {
                const uniqueTags = new Set<string>();
                let start = 0;
                let end = 0;

                while (end <= tagsString.length) {
                    if (end === tagsString.length || tagsString[end] === ",") {
                        if (start !== end) {
                            const tag = tagsString.slice(start, end).trim().toLowerCase();
                            if (tag) {
                                uniqueTags.add(tag);
                            }
                        }
                        start = end + 1;
                    }
                    end++;
                }

                tags = Array.from(uniqueTags);
                tagsCache.set(tagsString, tags);
            }
        }

        keywords[validKeywordsCount++] = {
            value: keywordValue,
            tags,
            searchEnginesIds: emptySearchEngines,
            sourceId: id,
            targetUrl: row[targetUrlIdx] || "",
        };
    }

    keywords.length = validKeywordsCount;

    return {
        type: "csv",
        id,
        name: fileName,
        keywords,
    };
}

/**
 * Generates Excel-style column names (A, B, ..., Z, AA, AB, ..., ZZ) for the input array.
 * Each returned column name is prefixed with "Column " (e.g., "Column A", "Column B").
 *
 * @param array - The source array for which to generate column names
 * @returns Array of column names in Excel style
 */
export function getExcelColumnNames(array: string[]): string[] {
    const columns: string[] = [];

    // Iterate over the input array indices
    for (let i = 0; i < array.length; i++) {
        let column = "";
        let n = i;

        // Convert the index to Excel column letter(s)
        // Example: 0 -> 'A', 1 -> 'B', ..., 25 -> 'Z', 26 -> 'AA', etc.
        while (n >= 0) {
            // Get the current character for the column name (A=65 in ASCII)
            column = String.fromCharCode(65 + (n % 26)) + column;
            // Move to the previous "power" of the alphabet (base 26)
            n = Math.floor(n / 26) - 1;
        }

        // Add the prefix "Column" to the Excel-style column name
        columns.push(`Column ${column}`);
    }

    return columns;
}
